// ---------------------------------------------------------------------------
// PWA_ControlBillet — Single scan history row
// Shows status icon, ticket ID, customer name, timestamp, offline badge.
// ---------------------------------------------------------------------------

import type { ScanEntry } from '@/lib/types';

interface Props {
  entry: ScanEntry & { status?: string };
}

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

function CheckSmallIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-success"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function XSmallIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-danger"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function WarningSmallIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-duplicate"
    >
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getStatusIcon(status: string | undefined) {
  switch (status) {
    case 'valid':
      return <CheckSmallIcon />;
    case 'invalid':
    case 'expired':
      return <XSmallIcon />;
    case 'duplicate':
      return <WarningSmallIcon />;
    default:
      return <CheckSmallIcon />;
  }
}

function getStatusBg(status: string | undefined): string {
  switch (status) {
    case 'valid':
      return 'bg-success/15';
    case 'invalid':
    case 'expired':
      return 'bg-danger/15';
    case 'duplicate':
      return 'bg-duplicate/15';
    default:
      return 'bg-success/15';
  }
}

function formatRelativeTime(timestamp: string): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  const seconds = Math.floor(diff / 1000);

  if (seconds < 10) return "a l'instant";
  if (seconds < 60) return `il y a ${seconds}s`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `il y a ${minutes} min`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `il y a ${hours}h`;

  const days = Math.floor(hours / 24);
  return `il y a ${days}j`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function HistoryItem({ entry }: Props) {
  const status = entry.status;

  return (
    <div className="flex items-center gap-3 bg-surface-card rounded-xl px-4 py-3 transition-transform duration-150 active:scale-[0.98]">
      {/* Status icon */}
      <div
        className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${getStatusBg(status)}`}
      >
        {getStatusIcon(status)}
      </div>

      {/* Middle: ticket info */}
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-bold truncate">
          {entry.ticketId}
        </p>
        {entry.customerName && (
          <p className="text-gray-400 text-xs truncate">
            {entry.customerName}
          </p>
        )}
        <p className="text-gray-500 text-[11px]">
          {formatRelativeTime(entry.timestamp)}
        </p>
      </div>

      {/* Right: offline badge */}
      {entry.offline && (
        <span className="shrink-0 bg-warning/20 text-warning text-[10px] font-semibold px-2 py-0.5 rounded-full">
          Offline
        </span>
      )}
    </div>
  );
}
